import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import {
  ArrowLeft,
  CalendarDays,
  MapPinned,
  Smartphone,
  StopCircle,
  XOctagon,
} from 'lucide-react'
import {
  CircleMarker,
  MapContainer,
  Polyline,
  Popup,
  TileLayer,
} from 'react-leaflet'
import 'leaflet/dist/leaflet.css'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/ui/card'
import { Kpi } from '@/components/ui/kpi'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { navPermissionCheck } from '@/components/layout/navVisibility'
import { useMe } from '@/features/auth/api'
import { Link, useParams } from '@/lib/router'
import { dateTime, relativeTime } from '@/lib/format'

import {
  type TripReviewItem,
  type TripTrailFix,
  useForceCloseTrip,
  useTrip,
  useTripTrail,
} from './api'
import { TripVisits } from './TripVisits'

/**
 * Trip Detail page (T4.1).
 *
 * Manager drills in from /trips/review on a single row, lands here, sees:
 *   - Header with status + agent + start/end times.
 *   - KPI tiles (Distance / Efficiency / Flagged fixes).
 *   - A Leaflet map with the polyline trail + start/end markers + a red
 *     dot per flagged fix.
 *
 * Trail and header both auto-refresh — header on a 30s cadence, trail
 * faster while the trip is ACTIVE (15s) so the manager can watch it
 * extend in near-real-time.
 */
export function TripDetailPage() {
  const { tripId } = useParams<{ tripId: string }>({ from: '/trips/$tripId' })

  const trip = useTrip(tripId)
  const isActive = trip.data?.status === 'ACTIVE'
  const trail = useTripTrail(tripId, { isActive })
  // Cosmetic: force-close checks TRIP_REQUEST_APPROVE and the agent's scope itself.
  const { data: me } = useMe()
  const canForceClose = navPermissionCheck(me)('TRIP_REQUEST_APPROVE')

  if (trip.isError) {
    return (
      <ErrorState
        title="Could not load visit plan"
        error={trip.error}
        backLabel="Back to Visit review"
      />
    )
  }
  if (trip.isLoading || !trip.data) {
    return <LoadingState />
  }
  const item = trip.data

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <Link
            to="/trips/review"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-1 h-3 w-3" />
            Visit review
          </Link>
          <h1 className="mt-1 flex items-center gap-2 text-xl font-semibold">
            <MapPinned className="h-5 w-5" />
            Visit plan <code className="font-mono text-base">{shortId(item.tripId)}</code>
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Agent <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">{shortId(item.agentUserId)}</code>
            {item.startedAt && (
              <>
                <span className="mx-2 text-slate-300">·</span>
                <CalendarDays className="mr-1 inline h-3 w-3" />
                {dateTime(item.startedAt)}
                <span className="ml-1 text-xs">
                  ({relativeTime(item.startedAt)})
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {item.status === 'ACTIVE' && canForceClose && <ForceCloseCta tripId={item.tripId} />}
          <StatusBadge status={item.status} />
        </div>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi
          label="Distance"
          value={formatDistance(item.actualDistanceM)}
          hint={
            item.status === 'ACTIVE' ? 'updating…' : 'completed visit plan total'
          }
        />
        <Kpi
          label="Efficiency"
          value={formatEfficiency(item.travelEfficiency)}
          hint="straight-line ÷ actual"
        />
        <Kpi
          label="Flagged fixes"
          value={item.integrityFlagCount}
          hint={
            item.integrityFlagCount > 0
              ? 'mock-location flags'
              : 'clean'
          }
        />
        <Kpi
          label="End reason"
          value={labelReason(item.endReason)}
          hint={
            item.endedAt
              ? `ended ${relativeTime(item.endedAt)}`
              : 'still active'
          }
        />
      </div>

      <Card>
        <CardHeader className="space-y-1">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            GPS trail
          </h2>
          <p className="text-xs text-muted-foreground">
            {trail.data
              ? `${trail.data.fixes.length} fix${trail.data.fixes.length === 1 ? '' : 'es'} captured. Flagged fixes shown as red dots.`
              : 'Loading trail…'}
            {isActive && trail.data && (
              <span className="ml-1 text-info">Live · refreshing every 15s.</span>
            )}
          </p>
        </CardHeader>
        <CardContent>
          {trail.isError ? (
            <Alert variant="destructive">
              <AlertTitle>Could not load trail</AlertTitle>
              <AlertDescription>
                {isAxiosError(trail.error)
                  ? trail.error.message
                  : String(trail.error)}
              </AlertDescription>
            </Alert>
          ) : (
            <TripTrailMap item={item} fixes={trail.data?.fixes ?? []} />
          )}
        </CardContent>
      </Card>

      <TripVisits tripId={item.tripId} isActive={isActive} />

      <Card>
        <CardHeader className="space-y-1">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Device snapshot
          </h2>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <DeviceRow
            icon={<Smartphone className="h-3 w-3" />}
            label="Device"
            value={item.deviceModel}
          />
          <DeviceRow
            icon={<Smartphone className="h-3 w-3" />}
            label="OS"
            value={item.osVersion}
          />
          <DeviceRow
            icon={<Smartphone className="h-3 w-3" />}
            label="App version"
            value={item.appVersion}
          />
          <DeviceRow
            icon={<StopCircle className="h-3 w-3" />}
            label="Start fix"
            value={
              item.startedLat && item.startedLng
                ? `${item.startedLat}, ${item.startedLng}`
                : null
            }
            monospace
          />
          <DeviceRow
            icon={<StopCircle className="h-3 w-3" />}
            label="End fix"
            value={
              item.endedLat && item.endedLng
                ? `${item.endedLat}, ${item.endedLng}`
                : null
            }
            monospace
          />
        </CardContent>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------

function TripTrailMap({
  item,
  fixes,
}: {
  item: TripReviewItem
  fixes: TripTrailFix[]
}) {
  const points = useMemo(
    () =>
      fixes
        .filter((f) => f.lat !== null && f.lng !== null)
        .map((f) => ({
          seq: f.seq,
          lat: parseFloat(f.lat as string),
          lng: parseFloat(f.lng as string),
          mocked: f.mockLocationFlag,
          recordedAt: f.deviceRecordedAt,
          accuracyM: f.accuracyM,
        }))
        .filter(
          (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng),
        ),
    [fixes],
  )

  const center: [number, number] | null = useMemo(() => {
    if (points.length > 0) {
      const mid = points[Math.floor(points.length / 2)]!
      return [mid.lat, mid.lng]
    }
    if (item.startedLat && item.startedLng) {
      return [parseFloat(item.startedLat), parseFloat(item.startedLng)]
    }
    return null
  }, [points, item.startedLat, item.startedLng])

  if (!center) {
    return (
      <div className="grid h-72 place-items-center rounded-md border border-dashed bg-slate-50 text-sm text-muted-foreground">
        No GPS fixes captured yet.
      </div>
    )
  }

  const flagged = points.filter((p) => p.mocked)
  const startedAt: [number, number] | null =
    item.startedLat && item.startedLng
      ? [parseFloat(item.startedLat), parseFloat(item.startedLng)]
      : null
  const endedAt: [number, number] | null =
    item.endedLat && item.endedLng
      ? [parseFloat(item.endedLat), parseFloat(item.endedLng)]
      : null

  return (
    <div className="h-[480px] overflow-hidden rounded-md border">
      <MapContainer
        center={center}
        zoom={14}
        scrollWheelZoom
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {points.length > 1 && (
          <Polyline
            positions={points.map((p) => [p.lat, p.lng] as [number, number])}
            pathOptions={{ color: '#1d4ed8', weight: 3, opacity: 0.85 }}
          />
        )}
        {startedAt && (
          <CircleMarker
            center={startedAt}
            radius={8}
            pathOptions={{
              color: '#15803d',
              fillColor: '#22c55e',
              fillOpacity: 0.9,
              weight: 2,
            }}
          >
            <Popup>Start fix</Popup>
          </CircleMarker>
        )}
        {endedAt && item.status !== 'ACTIVE' && (
          <CircleMarker
            center={endedAt}
            radius={8}
            pathOptions={{
              color: '#1d4ed8',
              fillColor: '#60a5fa',
              fillOpacity: 0.9,
              weight: 2,
            }}
          >
            <Popup>End fix</Popup>
          </CircleMarker>
        )}
        {flagged.map((p) => (
          <CircleMarker
            key={p.seq}
            center={[p.lat, p.lng]}
            radius={5}
            pathOptions={{
              color: '#b91c1c',
              fillColor: '#ef4444',
              fillOpacity: 0.85,
              weight: 1.5,
            }}
          >
            <Popup>
              <div className="text-xs">
                <div className="font-semibold text-red-700">
                  Flagged fix · seq {p.seq}
                </div>
                <div>{dateTime(p.recordedAt)}</div>
                {p.accuracyM && (
                  <div>Accuracy {p.accuracyM} m</div>
                )}
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  )
}

// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }) {
  const tone: 'positive' | 'info' | 'slate' =
    status === 'COMPLETED'
      ? 'positive'
      : status === 'ACTIVE'
        ? 'info'
        : 'slate'
  return <Badge tone={tone}>{status.replace(/_/g, ' ')}</Badge>
}

/**
 * Force-close trigger + inline confirmation panel.
 *
 * Inline rather than a modal so we don't drag in a Dialog primitive
 * for one screen. The panel is anchored top-right via a Portal-free
 * absolutely-positioned card — the trigger stays in the header, the
 * panel slides under it.
 */
function ForceCloseCta({ tripId }: { tripId: string }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const forceClose = useForceCloseTrip()

  async function submit() {
    setError(null)
    const trimmed = reason.trim()
    if (!trimmed) {
      setError('Reason is required for the audit log.')
      return
    }
    try {
      await forceClose.mutateAsync({ tripId, reason: trimmed })
      setOpen(false)
      setReason('')
    } catch (e) {
      const msg = isAxiosError(e)
        ? (e.response?.data as { message?: string })?.message ?? e.message
        : String(e)
      setError(msg || 'Force-close failed.')
    }
  }

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        className="border-negative/40 text-negative hover:bg-negative/10 hover:text-negative"
      >
        <XOctagon className="mr-1 h-3 w-3" />
        Force close
      </Button>
      {open && (
        <div className="absolute right-0 top-10 z-20 w-96 rounded-md border border-negative/40 bg-card p-4 shadow-lg">
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-negative">
                Force close these visits?
              </h3>
              <p className="text-xs text-muted-foreground">
                Status will flip to INTERRUPTED with reason FORCE_CLOSED. The
                agent's last buffered fix becomes the end coordinate.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="force-close-reason">Reason</Label>
              <Textarea
                id="force-close-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. agent unreachable for 4h, abandoned visits"
                rows={3}
              />
            </div>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setOpen(false)
                  setReason('')
                  setError(null)
                }}
                disabled={forceClose.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={submit}
                disabled={forceClose.isPending}
              >
                {forceClose.isPending ? 'Closing…' : 'Confirm force close'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function DeviceRow({
  icon,
  label,
  value,
  monospace = false,
}: {
  icon: React.ReactNode
  label: string
  value: string | null
  monospace?: boolean
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-slate-100 text-slate-500">
        {icon}
      </span>
      <span className="w-28 text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className={monospace ? 'font-mono text-xs' : ''}>
        {value ?? <span className="text-muted-foreground">—</span>}
      </span>
    </div>
  )
}

function ErrorState({
  title,
  error,
  backLabel,
}: {
  title: string
  error: unknown
  backLabel: string
}) {
  return (
    <div className="space-y-4">
      <Link
        to="/trips/review"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="mr-1 h-3 w-3" />
        {backLabel}
      </Link>
      <Alert variant="destructive">
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription>
          {/* The API answers 404 for "not yours to see" so it never reveals
              that the trip exists; say what the likely cause is. */}
          {isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 403)
            ? "You don't have the Visit details permission, or this visit is outside your scope."
            : isAxiosError(error)
              ? error.message
              : String(error)}
        </AlertDescription>
      </Alert>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="space-y-4">
      <div className="h-6 w-40 animate-pulse rounded bg-slate-200" />
      <div className="h-24 animate-pulse rounded bg-slate-100" />
      <div className="h-72 animate-pulse rounded bg-slate-100" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// formatters — kept here so the page is self-contained; we don't share with
// TripReviewPage to avoid the cross-file React-Refresh warning.

function formatDistance(metres: string | null): string {
  if (!metres) return '—'
  const n = parseFloat(metres)
  if (!Number.isFinite(n)) return '—'
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)} km`
  return `${Math.round(n)} m`
}

function formatEfficiency(eff: string | null): string {
  if (!eff) return '—'
  const n = parseFloat(eff)
  if (!Number.isFinite(n)) return '—'
  return `${Math.round(Math.max(0, Math.min(1, n)) * 100)}%`
}

function labelReason(code: string | null): string {
  if (!code) return '—'
  switch (code) {
    case 'USER_ENDED':
      return 'User ended'
    case 'FORCED_INTERRUPT':
      return 'Forced interrupt'
    case 'AUTO_NO_FIX':
      return 'Auto — no fix'
    case 'HARD_CAP_24H':
      return 'Auto — 24h cap'
    case 'FORCE_CLOSED':
      return 'Force-closed'
    default:
      return code.replace(/_/g, ' ').toLowerCase()
  }
}

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id
}

